import {
  RemoteDeviceVersion,
  VersionInfo,
  VersionType,
  DeviceType,
} from '../../../../types/firmware'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import { checkACK, DONGLE_MODEL_MAP, versionFileName } from '../../capabilities/memory'
import { CMD_VT_CH584, PROTOCOL_CONFIG_VT_CH584 } from '../../protocols/cmd'
import { processZip, FileEntry } from '../../../shared/firmware/shared/FileUtil'
import { compareVersions, formatCompactFirmwareVersion } from '../../../../utils/verify'
import { delay } from '../../../../utils/TimeUtils'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { usageType } from '../../../../types/manifest'
import { ChannelSelector } from '../../../../channels/ChannelRole'
import { ConnectionType } from '../../../../types/connection'
import { FirmwareEvents } from '../../../../../src/events/FirmwareEvents'
import { UPDATE_TYPE, WCH584Upgrader } from '../../../shared/firmware/wch/WCH584Upgrader'
import { isBaseDockContext } from '../../../../types/base'

const REPORT_ID = PROTOCOL_CONFIG_VT_CH584.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG_VT_CH584.feature_report_id

/**
 * 开启升级通道的重试策略。
 *
 * 设备收到「进入升级模式」命令后会重新枚举升级接口（VT7SE: usagePage 65392 / usage 113），
 * 命令返回后立即查找必然拿不到设备，因此先等一个稳定窗口，再按固定间隔轮询。
 * 取值与 QT `FirmwareUpgradeWCH585` 对齐：entryBootLoad 后 msleep(1000)，再以 1s 间隔最多查 6 次。
 */
const UPDATE_DEVICE_SETTLE_MS = 3000
const UPDATE_DEVICE_OPEN_ATTEMPTS = 6
const UPDATE_DEVICE_OPEN_INTERVAL_MS = 1000

export class VTCH584Firmware extends FirmwareFeature {
  private updateDevice: BaseDevice | null = null
  /** 进度缩放配置：当同时升级 WCH 和 Nordic 时，用于将各段 0-100% 原始进度映射到总进度的对应区间 */
  private progressScale?: { offset: number; range: number }

  private async getVersion(
    deviceSelect = 0,
    deviceType = DeviceType.Mouse,
  ): Promise<{ version: string; chipID?: number }> {
    const cmd =
      deviceType === DeviceType.Receiver
        ? CMD_VT_CH584.firmware_version[1]
        : CMD_VT_CH584.firmware_version[0]
    let payload = new Uint8Array([this.device.connectionType, cmd, deviceSelect])
    if (deviceType === DeviceType.Receiver) {
      payload = new Uint8Array([cmd, deviceSelect])
    }
    const res = await this.device.sendAndWait(
      REPORT_ID,
      payload,
      FEATURE_REPORT_ID,
      (res) => res.getUint8(0) !== 0x02,
    )
    checkACK(res)
    const version = res.getUint32(1, true)
    const chipID = res.getUint8(7)
    const sensorID = res.getUint8(8)

    if (
      deviceType === DeviceType.Receiver &&
      chipID === 5 &&
      sensorID === 0 &&
      !isBaseDockContext(this.device)
    ) {
      return {
        version: formatCompactFirmwareVersion(version, -1),
        chipID,
      }
    }

    return {
      version: formatCompactFirmwareVersion(version),
      chipID,
    }
  }
  async getFirmwareVersion(): Promise<VersionInfo[]> {
    const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
    // WCH-CH584 鼠标只有 1 颗沁恒主控(无 Nordic 副控);UI 统一展示 USB / MasterControl 两槽位,
    // 两槽位的 version 与 chipID 复用同一份 getVersion(0) 结果,协议层只发一次查询。
    const { version: wchVersion, chipID: wchChipID } = await this.getVersion(0, DeviceType.Mouse)
    const versions: VersionInfo[] = [
      {
        version: wchVersion,
        versionType: VersionType.USB,
        deviceType: DeviceType.Mouse,
        chipID: wchChipID,
      },
      {
        version: wchVersion,
        versionType: VersionType.MasterControl,
        deviceType: DeviceType.Mouse,
        chipID: wchChipID,
      },
    ]
    if (isWireless) {
      const { version: receiverWchVersion, chipID: receiverWchChipID } = await this.getVersion(
        0,
        DeviceType.Receiver,
      )
      versions.push(
        ...[
          {
            version: receiverWchVersion,
            versionType: VersionType.USB,
            deviceType: DeviceType.Receiver,
            chipID: receiverWchChipID,
          },
          {
            version: receiverWchVersion,
            versionType: VersionType.MasterControl,
            deviceType: DeviceType.Receiver,
            chipID: receiverWchChipID,
          },
        ],
      )
    }
    return versions
  }
  private async getVersionInfo(model: string): Promise<RemoteDeviceVersion> {
    //TODO 测试是用，正式上线前必须删除
    // this.device.__setFirmwareBaseUrl('https://soft.rapoo.com/RapooDriver/FirmwareTest')
    const timestamp = Date.now()
    const versionUrl = `${this.device.getFirmwareBaseUrl()}/${model}/${versionFileName}?t=${timestamp}`
    const versionRes = await fetch(versionUrl)
    if (!versionRes.ok) {
      throw new DeviceError(DeviceErrorCode.TIMEOUT, 'checkFirmwareUpdate failed')
    }
    const versionData = await versionRes.json()
    if (!versionData.Device || !versionData.Device.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'checkFirmwareUpdate failed')
    }
    return versionData.Device[0]
  }

  private resolveModel(): string | undefined {
    const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
    if (!isWireless) {
      return this.device.manifest.mainControl
    }
    return this.device.manifest.dongleModel
  }

  // 对getVersionInfo获取到的版本列表进行可更新判断
  async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const versions = await this.getFirmwareVersion()

    const model = this.device.manifest.mainControl ?? ''
    const versionFileInfo = await this.getVersionInfo(model)
    const receiverModel = this.resolveModel()
    const receiverVersionFileInfo = receiverModel ? await this.getVersionInfo(receiverModel) : null

    // N54H20只有一个芯片，所以这里直接MasterControl放Firmware
    return versions.map((current) => {
      const v = { ...current }
      v.canUpdate = false
      // 鼠标本体升级比较
      if (v.deviceType === DeviceType.Mouse) {
        const { FirmwareVersion } = versionFileInfo
        v.onlineVersion = FirmwareVersion
        v.canUpdate = FirmwareVersion ? this.canUpdateByVersion(FirmwareVersion, v.version) : false
      }
      // 接收器升级比较
      if (v.deviceType === DeviceType.Receiver && receiverVersionFileInfo) {
        const { FirmwareVersion } = receiverVersionFileInfo
        v.onlineVersion = FirmwareVersion
        v.canUpdate = FirmwareVersion ? this.canUpdateByVersion(FirmwareVersion, v.version) : false
      }
      //TODO 测试用
      // v.canUpdate = true
      return v
    })
  }

  async enterUpdateMode(isWireless: boolean): Promise<void> {
    let payload = new Uint8Array([0xff, 0xa8, 0x86, 0x79])
    if (isWireless) {
      payload = new Uint8Array([0xb1, 0x86, 0x79])
    }
    const res = await this.device.sendAndWait(
      REPORT_ID,
      payload,
      FEATURE_REPORT_ID,
      (res) => res.getUint8(0) !== 0x02,
    )
    checkACK(res)
  }

  /**
   * WCH-CH584 固件升级
   * @param url 固件地址
   */
  async updateFirmware(): Promise<void> {
    try {
      const MCU = this.device.manifest?.MCU
      if (!MCU) {
        throw new DeviceError(
          DeviceErrorCode.NOT_SUPPORTED_VERSION,
          'Update firmware failed: MCU not found',
        )
      }

      const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
      // 远程版本信息应基于“设备实际对应的 Dongle/主控”获取：
      // - 无线：以设备返回的 chipID 匹配 Dongle，命中则用该 Dongle，否则回退 dongleModel
      // - 有线：直接用 mainControl，chipID 仅用于无线模式下的 Dongle 匹配
      const versions = await this.getFirmwareVersion()
      console.log('versions', versions)
      const model = this.resolveModel()
      if (!model) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateFirmware failed')
      }
      const { FirmwareURL, TransparentURL } = await this.getVersionInfo(model)
      console.log('FirmwareURL', FirmwareURL)
      console.log('TransparentURL', TransparentURL)
      console.log('Wch584 固件升级')
      const url = `${this.device.getFirmwareBaseUrl()}/${model}/${FirmwareURL}`
      console.log('update url', url)

      await this.enterUpdateMode(isWireless)
      console.log('Wch584 已进入升级模式')
      await this.updateWchCh584(url, isWireless)
    } catch (error) {
      console.error(error)
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, JSON.stringify(error))
    } finally {
      this.releaseUpdateDevice()
      this.updateDevice = null
    }
  }

  private async updateWchCh584(url: string, isWireless: boolean): Promise<void> {
    try {
      const fileMap = await processZip(url)
      const firmwareData = Object.values(fileMap)[0]?.data as Uint8Array
      if (!firmwareData) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'firmwareData is empty')
      }

      this.updateDevice = await this.openFirmwareUpdateDevice('update')

      const upgrader = new WCH584Upgrader(this.updateDevice)

      // 设置进度回调
      upgrader.setOnProgressCallback((progress: number) => {
        const p = Math.min(100, Math.max(0, Math.floor(progress * 100) / 100))
        this.reportProgress(p)
      })

      // 设置完成回调
      upgrader.setOnProgressFinishCallback(() => {
        console.log('WCH-CH584 固件升级完成')
      })

      await upgrader.startUpdate(firmwareData, UPDATE_TYPE.SINGLE, isWireless)
      await upgrader.endDeviceUpdate()
    } catch (error) {
      console.error('WCH-CH584 固件升级失败:', error)
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `WCH-CH584 升级失败: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    } finally {
      await this.closeUpdateDevice({ usageType: 'update' })
      this.updateDevice = null
    }
  }
  /**
   * 上报进度，若存在 progressScale 则按等比缩放
   * @param rawProgress 原始进度 0-100
   */
  private reportProgress(rawProgress: number) {
    let scaledProgress = rawProgress
    if (this.progressScale) {
      // scaled = offset + range * (raw / 100)
      scaledProgress = this.progressScale.offset + this.progressScale.range * (rawProgress / 100)
    }
    this.device.emit(FirmwareEvents.WritedData, { progress: Math.floor(scaledProgress) })
  }

  /**
   * 打开固件升级通道。
   *
   * 设备收到「进入升级模式」命令后会重新枚举升级接口，此刻立即查找会拿到 null，
   * 因此这里先等一个稳定窗口再轮询，而不是单次尝试。
   *
   * 不传 requestIfMissing（默认 false）：只查已授权列表，避免 Web 端误弹 HID 选择框。
   * GCOME Boot 授权需弹窗时由调用方显式传 requestIfMissing: true。
   */
  private async openFirmwareUpdateDevice(usageType: usageType): Promise<BaseDevice> {
    await delay(UPDATE_DEVICE_SETTLE_MS)

    for (let attempt = 1; attempt <= UPDATE_DEVICE_OPEN_ATTEMPTS; attempt++) {
      const device: BaseDevice | null = await this.device.session.channels.openDevice({
        usageType,
      })
      if (device) return device

      console.warn(
        `[WCH-CH584] 升级接口未就绪，第 ${attempt}/${UPDATE_DEVICE_OPEN_ATTEMPTS} 次尝试失败`,
      )
      if (attempt < UPDATE_DEVICE_OPEN_ATTEMPTS) {
        await delay(UPDATE_DEVICE_OPEN_INTERVAL_MS)
      }
    }

    throw new DeviceError(
      DeviceErrorCode.OPEN_FAILED,
      `升级接口打开失败: usageType=${usageType}，已重试 ${UPDATE_DEVICE_OPEN_ATTEMPTS} 次`,
    )
  }

  private async releaseUpdateDevice(): Promise<void> {
    await this.device.session.channels.releaseDevice(this.updateDevice)
  }

  private async closeUpdateDevice(selector: ChannelSelector): Promise<void> {
    await this.device.session.channels.close(selector)
  }
}
